import React, { useEffect } from "react"
import { useForm } from "react-hook-form"
import { FirstNameField } from "./FirstNameField"
import { LastNameField } from "./LastNameField"
import { NicknameField } from "./NicknameField"
import { UploadAvatarSection } from "./UploadAvatarSection"
import { EmailField } from "./EmailField"
import { PhoneNumberField } from "./PhoneNumberField"
import { NotificationTable } from "./NotificationTable"
import { BandType } from "../BandEditPage/BandType"
import { EmailAddressCc } from "../BandEditPage/EmailAddressCc"
import { BandEditFormProvider } from "../BandEditPage/BandEditFormContext"
import { Band } from "../../types/bands"

interface User {
  id: number
  first_name: string
  last_name: string
  nickname: string
  email: string
  phone_number: string
  avatar_url?: string
  send_notification_emails: boolean
  send_notification_texts: boolean
  send_weekly_digest_emails: boolean
  send_discovery_emails: boolean
}

interface UserEditFormProps {
  user: User
  band?: Band
  bandStatus?: "idle" | "loading" | "error" | "success"
}

/** Wraps BandType in BandEditFormProvider so useBandEditForm is available (BandType expects band edit form context). */
const BandTypeWithProvider: React.FC<{ band: Band }> = ({ band }) => {
  const form = useForm<{ band_type: Band["band_type"] }>({
    defaultValues: { band_type: band.band_type },
  })
  useEffect(() => {
    form.reset({ band_type: band.band_type })
  }, [band.band_type, form])
  return (
    <BandEditFormProvider form={form} bandId={band.id}>
      <BandType band={band} />
    </BandEditFormProvider>
  )
}

/** Wraps EmailAddressCc in BandEditFormProvider so useBandEditForm is available (EmailAddressCc expects band edit form context). */
const EmailAddressCcWithProvider: React.FC<{ band: Band }> = ({ band }) => {
  const form = useForm<{ email_address_cc: string }>({
    defaultValues: { email_address_cc: band.email_address_cc || "" },
  })
  useEffect(() => {
    form.reset({ email_address_cc: band.email_address_cc || "" })
  }, [band.email_address_cc, form])
  return (
    <BandEditFormProvider form={form} bandId={band.id}>
      <EmailAddressCc band={band} />
    </BandEditFormProvider>
  )
}

export const UserEditForm: React.FC<UserEditFormProps> = ({
  user,
  band,
  bandStatus,
}) => {
  return (
    <>
      <form>
        <FirstNameField user={user} />
        <LastNameField user={user} />
        <NicknameField user={user} />
        {band && bandStatus === "success" && <BandTypeWithProvider band={band} />}
        <UploadAvatarSection user={user} />
        <h2 className="lg:col-span-8 col-span-12 font-bold border-b mb-4 border-black text-2xl uppercase mt-10">
          Email/Text Preferences
        </h2>
        <EmailField user={user} />
        <PhoneNumberField user={user} />
        {band && bandStatus === "success" && (
          <EmailAddressCcWithProvider band={band} />
        )}
        <NotificationTable user={user} />
      </form>
    </>
  )
}
